import { getSettings, loadRecoveryScript, LocalSettings } from "./settings";
import {
  getDismissData,
  getRenderData,
  hasRecovered,
  getConsentData,
  logRlinkScriptLoaded,
  invalidateUserData,
} from "./softwall/data";
import {
  getDismissHandler,
  onPageView,
  onPremiumUserDetected,
  onRecover,
  onBounce,
} from "./softwall/handlers";
import { RLinkAPI } from "./api";
import {
  adsAllowed,
  detectAdBlocker,
  detectPremiumABUser,
  getAADetectionResult,
  renderAllowed,
} from "./softwall/utils";
import { log } from "./softwall/utils/logs";

import type {
  AdBlockerType,
  BeforeUnloadOption,
  DismissData,
} from "./softwall/types";

(async () => {
  if (window.__bt_rlink_already_invoked) return;
  // Prevent rlink script from running multiple times.
  // This can happen if it's injected along with bt_tag in 'inject from tag' mode,
  //   or due to a bug causing an infinite script loading loop.

  logRlinkScriptLoaded();
  window.__bt_rlink_already_invoked = true;

  let settings: LocalSettings;
  try {
    settings = await getSettings();
  } catch (error) {
    log(
      "rlink.getSettingsError",
      {
        version: __SCRIPT_VERSION__,
        aaResults: window.__bt_intrnl.aaDetectionResults,
        error: error instanceof Error ? error.message : error,
        // I'm not sure if we need here settings
      },
      { severity: 4 },
    );
    settings = {} as LocalSettings;
  }

  const {
    allow_render_to_aa_users,
    hard_message_wall_mode_enabled,
    attribution_window_days,
  } = settings;
  const [aaDetectionResults, hasRendered, premiumDetectionResult, consentData] =
    await Promise.all([
      getAADetectionResult(),
      getRenderData(),
      detectPremiumABUser(settings),
      getConsentData(),
    ]).catch((error) => {
      log(
        "rlink.getDetectionError",
        {
          version: __SCRIPT_VERSION__,
          aaResults: window.__bt_intrnl.aaDetectionResults,
          error: error instanceof Error ? error.message : error,
          // I'm not sure if we need here settings
        },
        { severity: 4 },
      );

      return [];
    });

  let detectedExtension: AdBlockerType = "unknown";
  const adblockers: Record<string, AdBlockerType> = {
    adblockplus: "abp",
    adblock: "ab",
  };
  if (
    premiumDetectionResult?.extensionInfo &&
    premiumDetectionResult.extensionInfo.name in adblockers
  ) {
    detectedExtension = adblockers[premiumDetectionResult.extensionInfo.name];
  } else {
    detectedExtension = await detectAdBlocker();
  }

  window.__bt_intrnl.consentData = consentData;

  onPageView(
    aaDetectionResults,
    detectedExtension,
    consentData.consentShownInCurrentPV,
  );

  // if consent were not given script should stop processing
  // all data previously stored in local storage should be invalidated
  if (!consentData.isConsentGiven) {
    invalidateUserData();
    return;
  }

  // RL should not be rendered and process any RL events if:
  // - ads already allowed (AB disabled or AA enabled and rendering not allowed for AA users)
  // - consent message appears in the same page view
  const shouldNotBeRendered =
    adsAllowed(aaDetectionResults, allow_render_to_aa_users) ||
    consentData.consentShownInCurrentPV;

  // Stop RL for first run if it should not be rendered
  if (!hasRendered && shouldNotBeRendered) {
    loadRecoveryScript(settings);
    return;
  }

  const beforeUnloadHandler = () => {
    onBounce(detectedExtension, settings);
  };

  const beforeUnload = (opt: BeforeUnloadOption) => {
    if (opt === "subscribe") {
      window.addEventListener("beforeunload", beforeUnloadHandler);
    } else {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    }
  };

  const swRecovered = hasRecovered(
    settings,
    aaDetectionResults,
    premiumDetectionResult?.extensionInfo?.allowlistState,
  );

  console.log(swRecovered);
  console.log(premiumDetectionResult);

  // recovery event
  if (swRecovered) {
    if (swRecovered == "AA") {
      loadRecoveryScript(settings); // we should load recovery script if RL already recover user, as we are in charge of loading it
    }
    onRecover(swRecovered, detectedExtension, attribution_window_days);
    return;
  }

  // Always load recover script if we have SoftWall
  if (!hard_message_wall_mode_enabled) {
    loadRecoveryScript(settings);
  }

  const dismissHandler = getDismissHandler(detectedExtension, settings);
  const onDismiss = (dismissData: DismissData) => {
    beforeUnload("unsubscribe"); // if user dismiss the wall stop listen to beforeunload
    dismissHandler.onDismiss(dismissData);
  };

  // dismiss event
  const dismissData = await getDismissData();
  if (dismissData && !hard_message_wall_mode_enabled) {
    onDismiss(dismissData);
    return;
  }

  // handle premium ABP users
  if (premiumDetectionResult?.isPremium) {
    onPremiumUserDetected(premiumDetectionResult, detectedExtension);
    return;
  }

  // render RL if the rules for rendering are met
  if (!shouldNotBeRendered && (await renderAllowed(settings))) {
    import("./App").then(({ render }) => {
      const renderProps = {
        settings,
        onDismiss,
        beforeUnload,
        detectedExtension,
        aaDetectionResults,
      };
      const callRender = () => render(renderProps);
      if (settings.render_mode === "api") {
        new RLinkAPI(callRender, {
          mode: settings.api_mode,
        }).expose();
      } else {
        callRender();
      }
    });
  } else {
    loadRecoveryScript(settings);
  }
})();
